import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Mail, ArrowLeft, User as UserIcon, Upload, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import GoogleButton from '../components/GoogleButton';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrDivider = () => (
  <div className="flex items-center gap-3 my-4">
    <div className="flex-1 h-px bg-slate-200" />
    <span className="text-xs text-slate-400 font-medium">ou</span>
    <div className="flex-1 h-px bg-slate-200" />
  </div>
);

const RememberMe = ({ checked, onChange, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none" htmlFor={testid}>
    <input id={testid} data-testid={testid} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="accent-hm-orange w-4 h-4" />
    <span className="text-sm text-slate-600">Manter conectado</span>
  </label>
);

const AuthShell = ({ title, subtitle, children, badge }) => (
  <div className="min-h-screen flex flex-col lg:flex-row">
    <div className="lg:flex-1 hm-gradient text-white p-10 lg:p-16 flex flex-col justify-between relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 40%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="relative">
        <Link to="/" className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium"><ArrowLeft className="w-4 h-4" /> Voltar ao site</Link>
      </div>
      <div className="relative">
        <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">{badge}</span>
        <h1 className="mt-5 text-4xl lg:text-5xl font-extrabold leading-tight">{title}</h1>
        <p className="mt-3 text-white/90 max-w-md">{subtitle}</p>
      </div>
      <div className="relative text-sm text-white/80">© {new Date().getFullYear()} HM Talent</div>
    </div>
    <div className="lg:flex-1 bg-white flex items-center justify-center p-8">
      <div className="w-full max-w-md">{children}</div>
    </div>
  </div>
);

export const CandidatoLoginPage = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  useEffect(() => {
    if (location.state?.googleError) toast({ title: location.state.googleError, variant: 'destructive' });
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { ...form, role: 'candidate' });
      login(res.data.user, res.data.token, remember);
      toast({ title: 'Bem-vindo de volta!' });
      navigate('/portal/candidato');
    } catch (e) {
      toast({ title: 'Erro ao entrar', description: e.response?.data?.detail || 'Verifique seus dados.', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <AuthShell badge="Portal Candidato" title="Acesse sua conta" subtitle="Acompanhe suas candidaturas em tempo real e descubra novas oportunidades.">
      <h2 className="text-2xl font-extrabold text-slate-900">Entrar como candidato</h2>
      <p className="text-slate-600 mt-1">Use suas credenciais para acessar.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="candidato-login-email" required type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10 h-12" /></div>
        <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="candidato-login-password" required type="password" placeholder="Senha" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-10 h-12" /></div>
        <div className="flex items-center justify-between">
          <RememberMe testid="candidato-remember" checked={remember} onChange={setRemember} />
          <Link to="/recuperar-senha" data-testid="forgot-password-link" className="text-sm text-hm-orange font-semibold hover:underline">Esqueci minha senha</Link>
        </div>
        <Button type="submit" data-testid="candidato-login-submit" disabled={loading} className="w-full bg-hm-orange hover:bg-orange-600 rounded-full h-12 text-base">{loading ? 'Entrando...' : 'Entrar'}</Button>
      </form>
      <OrDivider />
      <GoogleButton portal="candidate" />
      <p className="text-sm text-slate-600 mt-5 text-center">Não tem conta? <Link to="/portal/candidato/cadastro" className="text-hm-orange font-semibold">Cadastre-se grátis</Link></p>
      <p className="text-xs text-slate-500 mt-3 text-center">É administrador? <Link to="/portal/admin/login" className="text-hm-orange font-semibold">Acesse o portal admin</Link></p>
    </AuthShell>
  );
};

export const CandidatoRegisterPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [lgpd, setLgpd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profilePrefill, setProfilePrefill] = useState(null);
  const [aiFile, setAiFile] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFileName, setAiFileName] = useState('');
  const [aiDone, setAiDone] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleAiUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiLoading(true); setAiFileName(file.name); setAiDone(false);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await axios.post(`${API}/ai/parse-resume-public`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const f = r.data.fields || {};
      setForm((prev) => ({ ...prev, name: f.name || prev.name, email: f.email || prev.email, phone: f.phone || prev.phone }));
      setProfilePrefill({ cpf: f.cpf, city: f.city, state: f.state, zip_code: f.zip_code, position_interest: f.position_interest, position_interests: f.position_interests, summary: f.summary, skills: f.skills, experiences: f.experiences });
      setAiFile(file);
      setAiDone(true);
      toast({ title: 'Currículo lido com sucesso!', description: 'Revise os dados, crie uma senha e finalize.' });
    } catch (err) {
      toast({ title: 'Não foi possível ler o currículo', description: err.response?.data?.detail || 'Tente outro arquivo (PDF, DOCX ou imagem).', variant: 'destructive' });
      setAiFileName('');
    }
    setAiLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!lgpd) { toast({ title: 'É necessário aceitar o termo LGPD para concluir o cadastro.', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      const payload = { ...form, lgpd_consent: lgpd };
      if (profilePrefill) payload.profile_prefill = profilePrefill;
      const res = await axios.post(`${API}/auth/register`, payload);
      login(res.data.user, res.data.token);
      if (aiFile && aiFile.type === 'application/pdf') {
        try {
          const cvFd = new FormData(); cvFd.append('file', aiFile);
          await axios.post(`${API}/profile/cv`, cvFd, { headers: { Authorization: `Bearer ${res.data.token}`, 'Content-Type': 'multipart/form-data' } });
        } catch (_) { /* currículo é opcional no cadastro */ }
      }
      toast({ title: 'Conta criada!', description: 'Complete seu perfil para se candidatar.' });
      navigate('/portal/candidato');
    } catch (e) {
      toast({ title: 'Erro ao cadastrar', description: e.response?.data?.detail || 'Tente novamente.', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <AuthShell badge="Portal Candidato" title="Crie sua conta grátis" subtitle="Cadastre-se em nosso banco de talentos e seja encontrado pelas melhores empresas.">
      <h2 className="text-2xl font-extrabold text-slate-900">Cadastro de candidato</h2>
      <p className="text-slate-600 mt-1">Leva menos de 1 minuto.</p>

      <div className="mt-5 p-4 rounded-2xl bg-orange-50 border border-orange-100">
        <div className="flex items-center gap-2 text-hm-orange font-bold text-sm"><Sparkles className="w-4 h-4" /> Cadastro rápido com IA</div>
        <p className="text-xs text-slate-600 mt-1">Anexe seu currículo (PDF, DOCX ou imagem) e a IA preenche o formulário para você revisar.</p>
        <label className="mt-3 inline-flex items-center gap-2 cursor-pointer bg-white border border-orange-200 rounded-full px-4 py-2 text-sm font-semibold text-hm-orange hover:bg-orange-100 transition-colors">
          <Upload className="w-4 h-4" /> {aiLoading ? 'Lendo currículo...' : (aiFileName || 'Cadastrar utilizando meu currículo')}
          <input data-testid="candidato-ai-upload" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" disabled={aiLoading} onChange={handleAiUpload} />
        </label>
        {aiDone && <p data-testid="candidato-ai-done" className="text-xs text-emerald-600 mt-2 font-medium">✓ Preenchemos seus dados! Revise abaixo, crie uma senha e finalize.</p>}
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="relative"><UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="candidato-register-name" required placeholder="Nome completo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pl-10 h-12" /></div>
        <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="candidato-register-email" required type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10 h-12" /></div>
        <Input data-testid="candidato-register-phone" placeholder="Telefone (WhatsApp)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" />
        <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="candidato-register-password" required type="password" placeholder="Crie uma senha (mín. 6 caracteres)" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-10 h-12" /></div>
        <label className="flex items-start gap-3 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl p-3 text-left">
          <input data-testid="register-lgpd-consent" type="checkbox" checked={lgpd} onChange={(e) => setLgpd(e.target.checked)} className="accent-hm-orange w-5 h-5 mt-0.5 shrink-0" />
          <span className="text-xs text-slate-600">Declaro estar ciente e concordo que meus dados pessoais, currículo e documentos poderão ser utilizados exclusivamente para processos seletivos e oportunidades profissionais conduzidas pela HM Talent, em conformidade com a Lei Geral de Proteção de Dados (LGPD).</span>
        </label>
        <Button type="submit" data-testid="candidato-register-submit" disabled={loading || !lgpd} className="w-full bg-hm-orange hover:bg-orange-600 rounded-full h-12 text-base disabled:opacity-60">{loading ? 'Cadastrando...' : 'Criar conta'}</Button>
      </form>
      <OrDivider />
      <GoogleButton portal="candidate" label="Cadastrar com Google" />
      <p className="text-sm text-slate-600 mt-5 text-center">Já tem conta? <Link to="/portal/candidato/login" className="text-hm-orange font-semibold">Faça login</Link></p>
    </AuthShell>
  );
};

export const AdminLoginPage = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  useEffect(() => {
    if (location.state?.googleError) toast({ title: location.state.googleError, variant: 'destructive' });
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { ...form, role: 'admin' });
      login(res.data.user, res.data.token, remember);
      toast({ title: 'Acesso liberado' });
      navigate(res.data.user.role === 'comercial' ? '/portal/comercial' : '/portal/admin');
    } catch (e) {
      toast({ title: 'Erro de autenticação', description: e.response?.data?.detail || 'Credenciais inválidas.', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <AuthShell badge="Área Restrita" title="Portal do Administrador" subtitle="Gerencie vagas, candidatos e candidaturas de toda a plataforma HM Talent.">
      <h2 className="text-2xl font-extrabold text-slate-900">Entrar como admin</h2>
      <p className="text-slate-600 mt-1">Acesso restrito a administradores.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="admin-login-email" required type="email" placeholder="E-mail admin" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10 h-12" /></div>
        <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="admin-login-password" required type="password" placeholder="Senha" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-10 h-12" /></div>
        <div className="flex items-center justify-between">
          <RememberMe testid="admin-remember" checked={remember} onChange={setRemember} />
          <Link to="/recuperar-senha" className="text-sm text-hm-orange font-semibold hover:underline">Esqueci minha senha</Link>
        </div>
        <Button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 rounded-full h-12 text-base">{loading ? 'Entrando...' : 'Entrar'}</Button>
      </form>
      <OrDivider />
      <GoogleButton portal="admin" label="Entrar com Google" />
    </AuthShell>
  );
};

export const EmpresaLoginPage = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { ...form, role: 'company' });
      sessionStorage.removeItem('hm_empresa_welcome');
      login(res.data.user, res.data.token, remember);
      navigate('/portal/empresa');
    } catch (e) {
      toast({ title: 'Erro de autenticação', description: e.response?.data?.detail || 'Credenciais inválidas.', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <AuthShell badge="Portal da Empresa" title="Portal da Empresa" subtitle="Acompanhe suas vagas, os candidatos enviados e o andamento de cada processo seletivo.">
      <h2 className="text-2xl font-extrabold text-slate-900">Entrar como empresa</h2>
      <p className="text-slate-600 mt-1">Use o acesso enviado pela HM Talent.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="empresa-login-email" required type="email" placeholder="E-mail da empresa" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10 h-12" /></div>
        <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="empresa-login-password" required type="password" placeholder="Senha" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="pl-10 h-12" /></div>
        <div className="flex items-center justify-between">
          <RememberMe testid="empresa-remember" checked={remember} onChange={setRemember} />
          <Link to="/recuperar-senha" className="text-sm text-hm-orange font-semibold hover:underline">Esqueci minha senha</Link>
        </div>
        <Button type="submit" data-testid="empresa-login-submit" disabled={loading} className="w-full bg-hm-orange hover:bg-orange-600 rounded-full h-12 text-base">{loading ? 'Entrando...' : 'Entrar'}</Button>
      </form>
      <p className="text-xs text-slate-500 mt-5 text-center">Procura oportunidades? <Link to="/portal/candidato/login" className="text-hm-orange font-semibold">Portal do Candidato</Link></p>
    </AuthShell>
  );
};


export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (e) {
      toast({ title: 'Erro ao solicitar redefinição', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <AuthShell badge="Recuperação de Senha" title="Esqueceu sua senha?" subtitle="Sem problemas! Enviaremos um link para você criar uma nova senha.">
      <h2 className="text-2xl font-extrabold text-slate-900">Recuperar acesso</h2>
      {sent ? (
        <div data-testid="forgot-success" className="mt-6 bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm">
          Se o e-mail estiver cadastrado, você receberá em instantes um link para redefinir sua senha. <strong>Confira também a caixa de spam.</strong>
        </div>
      ) : (
        <>
          <p className="text-slate-600 mt-1">Informe o e-mail cadastrado no portal.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="forgot-email" required type="email" placeholder="Seu e-mail" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 h-12" /></div>
            <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full bg-hm-orange hover:bg-orange-600 rounded-full h-12 text-base">{loading ? 'Enviando...' : 'Enviar link de redefinição'}</Button>
          </form>
        </>
      )}
      <p className="text-sm text-slate-600 mt-5 text-center"><Link to="/portal/candidato/login" className="text-hm-orange font-semibold">Voltar ao login</Link></p>
    </AuthShell>
  );
};

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast({ title: 'A senha deve ter ao menos 6 caracteres', variant: 'destructive' }); return; }
    if (pw !== pw2) { toast({ title: 'As senhas não coincidem', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      toast({ title: 'Senha redefinida!', description: 'Você já pode entrar com a nova senha.' });
      navigate('/portal/candidato/login');
    } catch (e) {
      toast({ title: 'Não foi possível redefinir', description: e.response?.data?.detail || 'Tente novamente.', variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <AuthShell badge="Nova Senha" title="Crie uma nova senha" subtitle="Defina uma senha forte para proteger sua conta no portal HM Talent.">
      <h2 className="text-2xl font-extrabold text-slate-900">Redefinir senha</h2>
      {!token ? (
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">Link inválido. Solicite uma nova redefinição na tela de login.</div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="reset-password" required type="password" placeholder="Nova senha (mín. 6 caracteres)" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} className="pl-10 h-12" /></div>
          <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input data-testid="reset-password-confirm" required type="password" placeholder="Confirme a nova senha" minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="pl-10 h-12" /></div>
          <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full bg-hm-orange hover:bg-orange-600 rounded-full h-12 text-base">{loading ? 'Salvando...' : 'Redefinir senha'}</Button>
        </form>
      )}
      <p className="text-sm text-slate-600 mt-5 text-center"><Link to="/portal/candidato/login" className="text-hm-orange font-semibold">Voltar ao login</Link></p>
    </AuthShell>
  );
};
